import { faThumbsUp } from '@fortawesome/pro-regular-svg-icons';
import { faFileArrowDown, faThumbsUp as faThumbsUpSolid } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { hasPermission } from 'Components/Authorize';
import LikeButton from 'Components/LikeButton';
import { Tip } from 'grommet';
import { useState } from 'preact/hooks';
import { useDispatch } from 'react-redux';
import { API_BASE_URL } from 'Shared/fetch';
import { logGAEvent } from 'Shared/ga';
import { trackMP } from 'Shared/mp';
import useFetch from 'use-http';
import { Permission } from 'zl-shared';

import { Tooltip } from '@/components/Tooltip';
import { updateMediaInQueryClient } from '@/queries/media';
import { useSelector } from '@/redux/helper';
import { selectCanDownloadMediaWithoutWatermark } from '@/redux/selectors/users';
import { updateAlbumData } from '@/redux/slices/myAlbum/actions';

import { androidDevice, getDeviceType, getShareableMediaUrl, iOSDevice } from '../../../helpers';
import { useIsMobileSize } from '../../../hooks';
import store from '../../../redux/store';
import { updateHabitatAlbumData } from '../../../routes/habitat/components/Album/actions';
import { CopyLink, DeleteMediaButton, DownloadButton, FacebookShareLink, WebShare } from './helper';

// overflow: 'visible' so the absolutely-positioned bubble (and its shadow) isn't clipped by the Drop.
// stretch: false so the Drop doesn't lock an inline width on the zero-width anchor.
const tipDropProps = { align: { bottom: 'top' }, overflow: 'visible', stretch: false };

const shareTipContent = (message) => (
  <div className="relative inline-block">
    {/* The Drop content is a zero-width anchor that grommet centers on the button; the bubble
        centers itself on that anchor via CSS, so it stays centered when the message width changes. */}
    <div className="preflight absolute bottom-0 left-1/2 -translate-x-1/2 -translate-y-0 whitespace-nowrap rounded-lg bg-white px-3 py-2 text-center text-sm text-black shadow-md">
      {message}
    </div>
  </div>
);

const ShareButtons = ({ mediaId, data, slug, slugPrefix, hashtags, userId, onDeleteSuccess }) => {
  const queryClient = useQueryClient();
  const { url, videoURL, habitatId, isLiked, usersLike, htmlURL, userId: ownerId, highlight } = data ?? {};
  const mediaType = data?.mediaType ?? data?.type;
  const videoType = data?.videoType ?? data?.type;
  const type = data?.type ?? videoType ?? mediaType;

  const dispatch = useDispatch();

  const role = useSelector((state) => state.user.role);
  const canDownloadMediaWithoutWatermark = useSelector(selectCanDownloadMediaWithoutWatermark);
  const isMobileSize = useIsMobileSize();
  const [isHighlighted, setIsHighlighted] = useState(highlight);
  const [linkCopied, setLinkCopied] = useState(false);
  const { post: sharePost } = useFetch(API_BASE_URL, {
    credentials: 'include',
    cachePolicy: 'no-cache',
  });
  const { put } = useFetch(API_BASE_URL, {
    credentials: 'include',
    cachePolicy: 'no-cache',
  });

  const logShare = async (platform) => {
    let habitatInfo = store.getState()?.habitat?.habitatInfo;
    if (videoType === 'trailer') {
      habitatInfo = store.getState()?.allHabitats?.filter((i) => i?._id === data?.habitat?.id)[0];
    }
    const shareLogObj = {
      userId: userId ?? 'guest',
      mediaId,
      mediaType: type,
      platform,
      slug,
      slugPrefix,
      deviceType: getDeviceType(),
      applicationPath: document.location.pathname.startsWith('/h') ? 'habitat' : 'publicAlbum',
      habitatId: habitatInfo?._id ?? habitatId,
    };
    trackMP('shared-ugc', shareLogObj);
    await sharePost('/logs/share', shareLogObj);
  };

  const onHighlightClick = async () => {
    const newHighLight = !isHighlighted;
    setIsHighlighted(newHighLight);
    const payload = { id: mediaId, data: { ...data, highlight: newHighLight } };
    dispatch(updateHabitatAlbumData(payload));
    dispatch(updateAlbumData(payload));

    try {
      await put(`/videos/${mediaId}`, { highlight: newHighLight });
      updateMediaInQueryClient(queryClient, mediaId, { highlight: newHighLight });
    } catch (e) {
      console.error('api error', e);
    }
  };

  return (
    <div className="flex w-full justify-between">
      <div className="mx-2 flex items-center gap-2">
        {/* Copy Link Button */}
        {(slugPrefix || htmlURL) && (
          <Tooltip
            dropProps={tipDropProps}
            content={shareTipContent(linkCopied ? 'Copied' : 'Copy to Share')}
            plain
            disabled={isMobileSize}
          >
            <div className="flex items-center">
              <CopyLink
                className="size-8 text-lg text-grey-1 hover:text-grey-2"
                link={getShareableMediaUrl(slugPrefix, mediaId) || htmlURL}
                onClick={() => logShare('shareLinkCopy')}
                onCopiedChange={setLinkCopied}
                showCopiedPopup={isMobileSize}
              />
            </div>
          </Tooltip>
        )}

        {/* Web Share API Button */}
        {(androidDevice() || iOSDevice()) && htmlURL && (
          <WebShare
            className="size-8 text-lg text-grey-1 hover:text-grey-2"
            logShare={logShare}
            width={20}
            radius={20}
            htmlURL={htmlURL}
          />
        )}

        {/* FB Share Button */}
        {htmlURL && (
          <Tooltip dropProps={tipDropProps} content={shareTipContent('Share to Facebook')} plain disabled={isMobileSize}>
            <div className="flex items-center">
              <FacebookShareLink
                className="size-8 text-lg text-grey-1 hover:text-grey-2"
                htmlURL={htmlURL}
                hashtags={hashtags}
                onClick={() => logShare('facebook')}
              />
            </div>
          </Tooltip>
        )}

        {/* Like Button */}
        <Tooltip
          dropProps={tipDropProps}
          content={shareTipContent(isLiked ? 'Remove from My Journal' : 'Add to My Journal')}
          plain
          disabled={isMobileSize}
        >
          <div className="flex items-center">
            <LikeButton
              noCount
              data={{ isLiked, likes: usersLike }}
              className={clsx('size-8 text-lg', isLiked ? 'text-system-pink' : 'text-grey-1 hover:text-grey-2')}
              onClick={() => logGAEvent('ugc', `react-liked-${type}`, slug, slugPrefix)}
              mediaId={mediaId}
              type={type}
            />
          </div>
        </Tooltip>

        {/* Download Button */}
        {(videoType === 'clip' || mediaType === 'photo') && (
          <Tooltip dropProps={tipDropProps} content={shareTipContent('Member-only Download')} plain disabled={isMobileSize}>
            <div className="flex items-center">
              <DownloadButton className="size-8 text-lg text-grey-1 hover:text-grey-2" url={url || videoURL} data={data} />
            </div>
          </Tooltip>
        )}
      </div>
      <div className="mx-2 flex items-center justify-center gap-2">
        {canDownloadMediaWithoutWatermark && (data?.rawVideoURL || data?.rawURL) && (
          <Tooltip dropProps={tipDropProps} content={shareTipContent('Download Raw File')} plain disabled={isMobileSize}>
            <div className="flex items-center">
              <DownloadButton
                className="size-8 text-lg text-system-pink"
                url={data?.rawVideoURL || data?.rawURL}
                data={data}
                icon={faFileArrowDown}
              />
            </div>
          </Tooltip>
        )}
        {['admin', 'moderator'].includes(role) && mediaType !== 'photo' && (
          <Tooltip
            dropProps={tipDropProps}
            content={shareTipContent(isHighlighted ? 'Hide on Home Page' : 'Promote to Home Page')}
            plain
            disabled={isMobileSize}
          >
            <div className="flex items-center">
              <button
                type="button"
                className="preflight preflight-button flex size-8 items-center justify-center text-lg text-system-pink"
                onClick={onHighlightClick}
              >
                <FontAwesomeIcon icon={isHighlighted ? faThumbsUpSolid : faThumbsUp} />
              </button>
            </div>
          </Tooltip>
        )}
        {/* Delete button */}
        {(ownerId === userId || hasPermission(Permission.Habitat.EditAlbumMediaHide)) && (
          <Tooltip dropProps={tipDropProps} content={shareTipContent('Delete Permanently')} plain disabled={isMobileSize}>
            <div className="flex items-center">
              <DeleteMediaButton
                className="size-8 text-lg text-system-pink"
                mediaId={mediaId}
                type={mediaType}
                onDeleteSuccess={onDeleteSuccess}
              />
            </div>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default ShareButtons;
